import React, { createContext, FC, ReactNode, useContext, useState } from "react";
import { Alert, AlertTitle } from "@mui/material";
import { ChiusureDialog, ChiusureProps } from "../builder/dialog/chiusure_dialog";
import { TeloDialog } from "../builder/dialog/telo_dialog";
import { Telo } from "../../../../models/pergola_configuration";

type DialogChiusureContextData = {
  selectChiusuraVetro: (data: ChiusureProps | null) => void;
  selectChiusuraCatenaccioli: (data: ChiusureProps | null) => void;
  selectChiusuraSerrature: (data: ChiusureProps | null) => void;

  selectTeloSemioscurantePVC: (data: { teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void }) => void;
  selectTeloOscurante: (data: { teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void }) => void;
  selectTeloSemioscurantePOLI: (data: { teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void }) => void;
};

const DialogChiusureContext = createContext<DialogChiusureContextData | null>(null);

const DialogChiusureProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const [chiusureDialog, setChiusureDialog] = useState<ChiusureProps | null>(null);
  const [teloDialog, setTeloDialog] = useState<{ teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void } | null>(null);
  const [errorDialog, setErrorDialog] = useState<{ title: string; message: string } | null>(null);

  const openDialogFiltered = (data: ChiusureProps | null, tipoFilter: string) => {
    if (!data) {
      setChiusureDialog(null);
      return;
    }

    const filteredChiusure =
      data.chiusure?.filter(ch => (ch.tipo ?? "").toLowerCase() === tipoFilter.toLowerCase()) || [];

    if (filteredChiusure.length === 0) {
      setErrorDialog({ title: "Attenzione", message: `Nessuna chiusura di tipo '${tipoFilter}' trovata.` });
      return;
    }

    setChiusureDialog({
      ...data,
      chiusure: filteredChiusure,
    });
  };

  const selectChiusuraVetro = (data: ChiusureProps | null) => openDialogFiltered(data, "Vetro Camera 25mm");
  const selectChiusuraCatenaccioli = (data: ChiusureProps | null) => openDialogFiltered(data, "CATENACCIOLI");
  const selectChiusuraSerrature = (data: ChiusureProps | null) => openDialogFiltered(data, "serrature");

  const openTeloDialogFiltered = (
    data: { teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void },
    stato: "PVC" | "OSCURANTE" | "SEMIOSCURANTE"
  ) => {
    const matching = data.teli.filter(t => {
      const code = t.tipo;
      if (stato === "PVC") return code.includes("PVC");
      if (stato === "OSCURANTE") return code.includes("OSCURANTE");
      if (stato === "SEMIOSCURANTE") return code.includes("POLI");
      return false;
    });

    if (matching.length === 0) {
      setErrorDialog({
        title: "Attenzione",
        message: `Nessun telo trovato per lo stato '${stato}'.`,
      });
      return;
    }

    setTeloDialog({
      ...data,
      teli: matching,
    });
  };

  const selectTeloSemioscurantePVC = (data: { teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void }) => {
    openTeloDialogFiltered(data, "PVC");
  };

  const selectTeloOscurante = (data: { teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void }) => {
    openTeloDialogFiltered(data, "OSCURANTE");
  };

  const selectTeloSemioscurantePOLI = (data: { teli: Telo[], selectedTelo: string | null, onConfirm: (code: string | null) => void }) => {
    openTeloDialogFiltered(data, "SEMIOSCURANTE");
  };

  return (
    <DialogChiusureContext.Provider value={{
      selectChiusuraVetro,
      selectChiusuraCatenaccioli,
      selectChiusuraSerrature,
      selectTeloSemioscurantePVC,
      selectTeloOscurante,
      selectTeloSemioscurantePOLI,
    }}>
      {children}

      {chiusureDialog && (
        <ChiusureDialog
          data={chiusureDialog}
          onConfirm={(value) => {
            chiusureDialog.onConfirm(value);
            setChiusureDialog(null);
          }}
          onCancel={() => setChiusureDialog(null)}
        />
      )}

      {teloDialog && (
        <TeloDialog
          teli={teloDialog.teli}
          selectedTelo={teloDialog.selectedTelo}
          onConfirm={(code) => {
            teloDialog.onConfirm(code);
            setTeloDialog(null);
          }}
          onCancel={() => setTeloDialog(null)}
        />
      )}

      {errorDialog && (
        <div
          className="d-flex justify-content-center py-3"
          style={{ position: "fixed", left: 0, bottom: 0, width: "100%" }}
        >
          <Alert
            variant="filled"
            severity="error"
            sx={{ zIndex: 1500 }}
            onClose={() => setErrorDialog(null)}
          >
            <AlertTitle>{errorDialog.title}</AlertTitle>
            {errorDialog.message}
          </Alert>
        </div>
      )}
    </DialogChiusureContext.Provider>
  );
};

export default DialogChiusureProvider;

export const useChiusureDialogs = () => {
  return useContext(DialogChiusureContext);
};
